import React from 'react';
import { Stack, Typography } from '@mui/material';

import DiscountCode from 'renderer/features/purchase-page/components/DiscountCode';

import type { DiscountDescriptor } from './cartTotals';

const styles = {
  heading: {
    marginTop: '0',
    marginBottom: '25px',
    fontSize: '24px',
    fontWeight: 'bold',
    borderBottom: '1px solid #ccc',
  } as React.CSSProperties,
  summaryBox: {
    backgroundColor: '#fff',
    borderRadius: '8px',
    padding: '20px',
  } as React.CSSProperties,
  checkoutButton: {
    width: '100%',
    padding: '12px',
    backgroundColor: '#00a9e0',
    color: '#fff',
    fontWeight: 'bold',
    border: 'none',
    borderRadius: '6px',
    cursor: 'pointer',
    marginTop: '20px',
  } as React.CSSProperties,
  supportBox: {
    backgroundColor: '#fff',
    borderRadius: '8px',
    padding: '20px',
    fontSize: '0.9rem',
    color: '#333',
  } as React.CSSProperties,
};

export interface CartOrderSummaryProps {
  itemsCount: number;
  baseTotal: number;
  grandTotal: number;
  totalDiscount: number;
  discountedLinesLength: number;
  discountDescriptors: DiscountDescriptor[];
  saleBeatsAppliedDiscount?: boolean;
  hasInvalidUpgradeSerials: boolean;
  checkoutDisabled: boolean;
  onCheckout: () => void;
  appliedDiscountCode: string;
  onDiscountUpdate: (summary: {
    totalBeforeDiscount: number;
    totalDiscount: number;
    totalAfterDiscount: number;
  }) => void;
}

export function CartOrderSummary({
  itemsCount,
  baseTotal,
  grandTotal,
  totalDiscount,
  discountedLinesLength,
  discountDescriptors,
  saleBeatsAppliedDiscount = false,
  hasInvalidUpgradeSerials,
  checkoutDisabled,
  onCheckout,
  appliedDiscountCode,
  onDiscountUpdate,
}: CartOrderSummaryProps) {
  return (
    <Stack spacing={2}>
      <div style={styles.summaryBox}>
        <h2 style={styles.heading}>SUMMARY</h2>
        <div style={{ display: 'grid', rowGap: 8 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            <span>
              {itemsCount} {itemsCount === 1 ? 'item' : 'items'}
            </span>
            <span>${baseTotal.toFixed(2)}</span>
          </div>

          {discountedLinesLength > 0 && totalDiscount > 0 && (
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                color: '#428A48',
                fontWeight: 600,
                alignItems: 'baseline',
                gap: 8,
              }}
            >
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  lineHeight: 1.2,
                }}
              >
                <span>Discount</span>
                {discountDescriptors.length > 0 && (
                  <div
                    style={{
                      fontWeight: 500,
                      fontSize: 12,
                      opacity: 0.9,
                      marginTop: 2,
                    }}
                  >
                    {discountDescriptors.map((d) => (
                      <div key={d.key} style={{ lineHeight: 1.2 }}>
                        {d.text}
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <span>- ${totalDiscount.toFixed(2)}</span>
            </div>
          )}

          {hasInvalidUpgradeSerials && (
            <Typography sx={{ mt: 1, color: 'error.main', fontSize: 12 }}>
              One or more upgrade serials are invalid. Please correct them
              before proceeding to checkout.
            </Typography>
          )}
        </div>

        <div
          style={{
            marginTop: 10,
            display: 'flex',
            justifyContent: 'space-between',
            fontWeight: 800,
            fontSize: '18px',
            background: '#f1f1f1',
            padding: '10px 12px',
            borderRadius: 6,
          }}
        >
          <span>Total</span>
          <span>${grandTotal.toFixed(2)}</span>
        </div>

        <Stack justifyContent="flex-end" spacing={1} sx={{ mt: 2 }}>
          <Stack justifyContent="flex-end">
            <DiscountCode
              key={`codes-${discountedLinesLength}`}
              initialCode={appliedDiscountCode || ''}
              onDiscountUpdate={onDiscountUpdate}
              saleBeatsAppliedDiscount={saleBeatsAppliedDiscount}
            />
          </Stack>
        </Stack>

        <button
          type="button"
          onClick={() => {
            if (checkoutDisabled) return;
            onCheckout();
          }}
          style={{
            ...styles.checkoutButton,
            opacity: checkoutDisabled ? 0.6 : 1,
            cursor: checkoutDisabled ? 'not-allowed' : 'pointer',
          }}
        >
          PROCEED TO CHECKOUT
        </button>
      </div>

      <div style={styles.supportBox}>
        <p>
          <b>Trouble Checking Out?</b>
        </p>
        <p>
          You can also purchase at{' '}
          <a
            href="https://store.digitalanarchy.com"
            target="_blank"
            rel="noreferrer"
            style={{ textDecoration: 'none' }}
          >
            store.digitalanarchy.com
          </a>{' '}
          or contact us.
        </p>
        <p>
          📞 <b>415-287-6069</b>, 10am–5pm PST
        </p>
        <p>
          <b>Digital Downloads</b>
        </p>
        <p>
          All products/invoices are emailed to you. Check spam! Don&apos;t see
          it? Email <b>sales@digitalanarchy.com</b>.
        </p>
      </div>
    </Stack>
  );
}
